import React from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DialogFooter } from '../components/ui/dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useCanSeeRevenue } from '../lib/perms';

/**
 * CandidateFormContent - Extracted as a separate component to prevent
 * scroll issues caused by React re-renders when form state changes.
 * 
 * This component is stable and won't cause the dialog to scroll up
 * when typing in fields.
 */
const CandidateFormContent = ({
  formData,
  setFormData,
  onSubmit,
  submitText,
  isEdit = false,
  formLoading = false,
  showDuplicatesWarning = false,
  duplicates = [],
  checkingDuplicates = false,
  setShowDuplicatesWarning,
}) => {
  const canSeeRevenue = useCanSeeRevenue();
  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      {/* Duplicate Warning */}
      {showDuplicatesWarning && duplicates.length > 0 && !isEdit && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="duplicate-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">Potential Duplicates Found</p>
              <p className="text-sm text-amber-700 mb-2">
                {duplicates.length} similar candidate(s) already exist:
              </p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {duplicates.slice(0, 3).map((dup) => (
                  <div key={dup.id} className="text-sm text-amber-700 flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-white">
                      {(dup.match_type || 'match').replace('_', ' ')}
                    </Badge>
                    <span>{dup.name || 'Unnamed'}</span>
                    {dup.email && <span className="text-amber-600">({dup.email})</span>}
                    <span className="text-amber-500">- {Math.round(dup.match_score || 0)}% match</span>
                  </div>
                ))}
                {duplicates.length > 3 && (
                  <p className="text-xs text-amber-600">+{duplicates.length - 3} more...</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-amber-700 hover:text-amber-800 p-0 h-auto"
                onClick={() => setShowDuplicatesWarning(false)}
              >
                Dismiss warning
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Full name"
            data-testid="candidate-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <div className="relative">
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
              data-testid="candidate-email-input"
            />
            {checkingDuplicates && (
              <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 234 567 8900"
            data-testid="candidate-phone-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Location</Label>
          <Input
            value={formData.current_location}
            onChange={(e) => setFormData({ ...formData, current_location: e.target.value })}
            placeholder="City, Country"
            data-testid="candidate-location-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Experience (years)</Label>
          <Input
            type="number"
            value={formData.experience_years}
            onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
            min={0}
            step={0.5}
            data-testid="candidate-exp-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Current Company</Label>
          <Input
            value={formData.current_company}
            onChange={(e) => setFormData({ ...formData, current_company: e.target.value })}
            placeholder="Company name"
            data-testid="candidate-company-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Current Designation</Label>
          <Input
            value={formData.current_designation}
            onChange={(e) => setFormData({ ...formData, current_designation: e.target.value })}
            placeholder="Job title"
            data-testid="candidate-designation-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Education</Label>
          <Input
            value={formData.education}
            onChange={(e) => setFormData({ ...formData, education: e.target.value })}
            placeholder="Highest qualification"
            data-testid="candidate-education-input"
          />
        </div>
      </div>

      {/* Salary & Notice Period — CTC fields are admin-only (revenue info) */}
      <div className="grid grid-cols-3 gap-4">
        {canSeeRevenue && (
        <div className="space-y-2">
          <Label>Current CTC (Lakhs)</Label>
          <Input
            type="number"
            value={formData.current_ctc}
            onChange={(e) => setFormData({ ...formData, current_ctc: e.target.value })}
            placeholder="e.g., 12.5"
            min={0}
            step={0.5}
            data-testid="candidate-current-ctc-input"
          />
        </div>
        )}
        {canSeeRevenue && (
        <div className="space-y-2">
          <Label>Expected CTC (Lakhs)</Label>
          <Input
            type="number"
            value={formData.expected_ctc}
            onChange={(e) => setFormData({ ...formData, expected_ctc: e.target.value })}
            placeholder="e.g., 15"
            min={0}
            step={0.5}
            data-testid="candidate-expected-ctc-input"
          />
        </div>
        )}
        <div className="space-y-2">
          <Label>Notice Period (Days)</Label>
          <Input
            type="number"
            value={formData.notice_period}
            onChange={(e) => setFormData({ ...formData, notice_period: e.target.value })}
            placeholder="e.g., 30"
            min={0}
            data-testid="candidate-notice-period-input"
          />
        </div>
      </div>

      {/* Date of Birth (age is computed automatically from this) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-1">
            Date of Birth
            <span className="text-xs text-slate-400 font-normal">(optional · age auto-computed)</span>
          </Label>
          <Input
            type="date"
            value={formData.date_of_birth || ''}
            onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
            max={new Date().toISOString().slice(0, 10)}
            data-testid="candidate-dob-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Computed Age</Label>
          <div
            className="h-10 px-3 flex items-center text-sm text-slate-600 bg-slate-50 rounded-md border border-slate-200"
            data-testid="candidate-age-preview"
          >
            {(() => {
              const dob = formData.date_of_birth;
              if (!dob) return <span className="text-slate-400 italic">Set DOB to see age</span>;
              try {
                const d = new Date(dob);
                if (Number.isNaN(d.getTime())) return <span className="text-slate-400 italic">Invalid date</span>;
                const today = new Date();
                let age = today.getFullYear() - d.getFullYear();
                const m = today.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
                if (age < 0 || age > 120) return <span className="text-slate-400 italic">—</span>;
                return <span className="font-semibold">{age} years</span>;
              } catch {
                return <span className="text-slate-400 italic">—</span>;
              }
            })()}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Skills (comma-separated)</Label>
        <Input
          value={formData.skills}
          onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
          placeholder="React, Node.js, Python"
          data-testid="candidate-skills-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes..."
          rows={2}
          data-testid="candidate-notes-input"
        />
      </div>

      <DialogFooter>
        <Button
          onClick={onSubmit}
          disabled={formLoading || !formData.name}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid="candidate-submit-btn"
        >
          {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {submitText}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default CandidateFormContent;
